import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import { cx } from '@/lib/cx';

export type ToastTone = 'success' | 'error' | 'info' | 'warning';

export interface ToastInput {
  tone?: ToastTone;
  title: ReactNode;
  message?: ReactNode;
  /** 自動で閉じるまでの ms。0 で閉じない（error の既定は 0） */
  duration?: number;
}

interface ToastItem extends Required<Pick<ToastInput, 'tone' | 'title'>> {
  id: number;
  message?: ReactNode;
}

interface ToastApi {
  show: (t: ToastInput) => number;
  success: (title: ReactNode, message?: ReactNode) => number;
  error: (title: ReactNode, message?: ReactNode) => number;
  info: (title: ReactNode, message?: ReactNode) => number;
  warning: (title: ReactNode, message?: ReactNode) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const ICONS: Record<ToastTone, ReactNode> = {
  success: <CheckCircle2 size={18} />,
  error: <AlertCircle size={18} />,
  info: <Info size={18} />,
  warning: <TriangleAlert size={18} />,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);

  const show = useCallback(
    ({ tone = 'info', title, message, duration }: ToastInput) => {
      const id = ++seq.current;
      setItems((xs) => [...xs.slice(-4), { id, tone, title, message }]);
      const ms = duration ?? (tone === 'error' ? 0 : 4000);
      if (ms > 0) setTimeout(() => dismiss(id), ms);
      return id;
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      success: (title, message) => show({ tone: 'success', title, message }),
      error: (title, message) => show({ tone: 'error', title, message }),
      info: (title, message) => show({ tone: 'info', title, message }),
      warning: (title, message) => show({ tone: 'warning', title, message }),
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="bs-toasts" aria-live="polite" aria-relevant="additions">
          {items.map((t) => (
            <div key={t.id} role={t.tone === 'error' ? 'alert' : 'status'} className={cx('bs-toast', `bs-toast--${t.tone}`)}>
              <span className="bs-toast__icon" aria-hidden>
                {ICONS[t.tone]}
              </span>
              <div className="bs-toast__body">
                <div className="bs-toast__title">{t.title}</div>
                {t.message && <div className="bs-toast__message">{t.message}</div>}
              </div>
              <button type="button" className="bs-toast__close" aria-label="通知を閉じる" onClick={() => dismiss(t.id)}>
                <X size={15} />
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

/** 通知を表示する。`const toast = useToast(); toast.success('保存しました');` */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast は ToastProvider の内側で使用してください');
  return ctx;
}
